import { Link } from "react-router-dom";
import {
  ArrowRight,
  TrendingUp,
  Handshake,
  Target,
  Globe2,
  Users,
  ClipboardCheck,
  Rocket,
  Search,
} from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const CONSULT_IMG =
  "https://images.unsplash.com/photo-1624555130581-1d9cca783bc0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const BROKER_IMG =
  "https://images.unsplash.com/photo-1578574577315-3fbeb0cecdc2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo
        title="Our Services"
        description="Franchise consulting and partner broking for knowledge businesses. We make brands franchise-ready, design scalable models, and recruit the right franchise partners to grow globally."
        path="/services"
      />
      {/* HEADER */}
      <section className="bg-navy noise-overlay text-white pt-20 pb-24 md:pt-28 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel className="text-gold">Our Services</SectionLabel>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-3xl text-balance">
              We make knowledge businesses franchise-ready — then help them grow.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
              Two complementary capabilities designed to scale your brand across
              global markets: strategic consulting and partner broking.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CONSULTING */}
      <section className="py-20 md:py-32 bg-canvas">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="text-sm uppercase tracking-[0.2em] font-semibold text-ink-muted">
              01 — Consulting
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-semibold text-navy">
              Scaling &amp; franchise readiness.
            </h2>
            <p className="mt-6 text-lg text-ink-secondary leading-relaxed">
              We focus on scaling knowledge-based businesses across global
              markets — making them franchise-ready and helping them expand with
              confidence. From building the franchise model to entering new
              markets, we are hands-on partners in your growth.
            </p>
            <div className="mt-8 space-y-5">
              {[
                { icon: Target, t: "Franchise Model Design", d: "Structuring your business into a scalable, replicable franchise system." },
                { icon: Globe2, t: "Market-Entry Strategy", d: "Localised go-to-market plans tailored to each target market." },
                { icon: Rocket, t: "Expansion & Scale", d: "Ongoing support to grow your footprint sustainably and profitably." },
              ].map((f) => (
                <div key={f.t} className="flex gap-4">
                  <div className="h-11 w-11 shrink-0 flex items-center justify-center bg-navy text-gold">
                    <f.icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy text-lg">{f.t}</h3>
                    <p className="text-ink-secondary">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative">
              <div className="absolute -top-5 -right-5 h-28 w-28 bg-teal z-0" />
              <img src={CONSULT_IMG} alt="Consulting" className="relative z-10 w-full h-[520px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* BROKERING */}
      <section className="py-20 md:py-32 bg-canvas-alt">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal className="lg:order-2">
            <span className="text-sm uppercase tracking-[0.2em] font-semibold text-ink-muted">
              02 — Brokering
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-semibold text-navy">
              Recruiting the right partners.
            </h2>
            <p className="mt-6 text-lg text-ink-secondary leading-relaxed">
              Growth needs the right people. We help brands recruit franchise
              partners — identifying, qualifying and onboarding investors who share
              the vision and have the capability to deliver.
            </p>
            <div className="mt-8 space-y-5">
              {[
                { icon: Search, t: "Partner Sourcing", d: "Reaching and matching qualified investors to your opportunity." },
                { icon: ClipboardCheck, t: "Due Diligence", d: "Careful vetting to ensure a strong brand-partner fit." },
                { icon: Users, t: "Onboarding", d: "Smooth handover so new partners launch with momentum." },
              ].map((f) => (
                <div key={f.t} className="flex gap-4">
                  <div className="h-11 w-11 shrink-0 flex items-center justify-center bg-navy text-gold">
                    <f.icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy text-lg">{f.t}</h3>
                    <p className="text-ink-secondary">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:order-1">
            <div className="relative">
              <div className="absolute -bottom-5 -left-5 h-28 w-28 bg-gold z-0" />
              <img src={BROKER_IMG} alt="Brokering" className="relative z-10 w-full h-[520px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 md:py-32 bg-canvas">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel>Our Approach</SectionLabel>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold text-navy max-w-2xl">
              A clear path from concept to expansion.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { n: "01", icon: TrendingUp, t: "Assess", d: "Evaluate the brand's franchise potential and readiness." },
              { n: "02", icon: Target, t: "Build", d: "Design the model, systems and market-entry plan." },
              { n: "03", icon: Handshake, t: "Match", d: "Source and onboard the right franchise partners." },
              { n: "04", icon: Rocket, t: "Scale", d: "Support expansion across global markets." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="bg-white border border-navy/10 p-8 h-full">
                  <div className="flex items-center justify-between">
                    <span className="text-4xl font-bold text-navy/10 font-heading">{s.n}</span>
                    <s.icon className="text-teal" size={24} />
                  </div>
                  <h3 className="mt-4 text-xl font-semibold text-navy">{s.t}</h3>
                  <p className="mt-2 text-ink-secondary">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-dark text-white py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl font-semibold">Let's build your franchise story.</h2>
            <Link
              to="/contact"
              data-testid="services-cta"
              className="mt-8 inline-flex items-center gap-2 bg-gold text-navy px-10 py-4 font-bold hover:bg-yellow-400 transition-colors group"
            >
              Get In Touch
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
